/** A page's code is fetched on first visit (`routes/index.tsx`). After a
 *  deploy the file names change, so a tab opened before it asks for a file
 *  that is gone and the page crashed with "Failed to fetch dynamically
 *  imported module" until the customer reloaded by hand (platform test round
 *  3, first visit to Auto scaling groups). Reloading fetches the new index and
 *  with it the new names — so do that, once.
 */

const RELOADED_AT_KEY = "omnicloud:page-code-reloaded-at";
/** A second failure this soon after a reload is not a stale tab: show the
 *  error instead of reloading for ever. */
const RELOAD_GUARD_MS = 60_000;

/** The browsers' wordings for "the module file could not be fetched". */
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading (CSS )?chunk [^ ]+ failed|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_LOAD_ERROR.test(error.message);
}

function reloadedAt(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
  } catch {
    // No session storage (blocked): cannot tell a loop, so never reload.
    return Number.POSITIVE_INFINITY;
  }
}

/**
 * Wrap a dynamic import for `React.lazy`: when the page's code cannot be
 * fetched, reload the tab once and keep the loading skeleton up meanwhile.
 * If it fails again within a minute of that reload, the error is thrown for
 * the error boundary — a reload did not help and another would only loop.
 */
export function loadWithReload<T>(
  load: () => Promise<T>,
  reload: () => void = () => window.location.reload(),
): () => Promise<T> {
  return () =>
    load().catch((error: unknown) => {
      if (!isChunkLoadError(error)) throw error;
      const last = reloadedAt();
      if (Date.now() - last < RELOAD_GUARD_MS) throw error;
      try {
        window.sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
      } catch {
        throw error;
      }
      reload();
      return new Promise<T>(() => {});
    });
}
